import {
  MIN_MODAL_HEIGHT,
  MIN_MODAL_WIDTH,
  MODAL_BOTTOM_OFFSET,
  MODAL_RIGHT_OFFSET,
  UNIQ_MODAL_SIZE_STORAGE_KEY,
} from "../constants";

export type UniQModalSize = {
  width: number;
  height: number;
};

export const getModalSizeStorageKey = (userId?: string): string => {
  return `${UNIQ_MODAL_SIZE_STORAGE_KEY}_${userId || "anonymous"}`;
};

/**
 * @description - Parses the stored modal size from a JSON string.
 * @param storedValue - The JSON string representing the modal size.
 * @returns The parsed modal size or null if parsing fails.
 */
export const parseStoredModalSize = (
  storedValue: string | null,
): UniQModalSize | null => {
  if (!storedValue) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(storedValue);
    const width = Number(parsedValue?.width);
    const height = Number(parsedValue?.height);

    if (!Number.isFinite(width) || !Number.isFinite(height)) {
      return null;
    }

    return {
      width: Math.max(width, MIN_MODAL_WIDTH),
      height: Math.max(height, MIN_MODAL_HEIGHT),
    };
  } catch (error) {
    console.warn("Failed to parse UniQ modal size from localStorage:", error);
    return null;
  }
};

/**
 * @description - Loads the stored modal size from localStorage for a given user.
 * @param userId - The ID of the user to load the modal size for.
 * @returns The loaded modal size or null if it cannot be loaded.
 */
export const loadStoredModalSize = (userId?: string): UniQModalSize | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const storageKey = getModalSizeStorageKey(userId);
    return parseStoredModalSize(localStorage.getItem(storageKey));
  } catch (error) {
    console.warn("Failed to load UniQ modal size from localStorage:", error);
    return null;
  }
};

/**
 * @description - Saves the given modal size to localStorage for a given user.
 * @param size - The modal size to save.
 * @param userId - The ID of the user to save the modal size for.
 * @returns void
 */
export const saveStoredModalSize = (
  size: UniQModalSize,
  userId?: string,
): void => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const storageKey = getModalSizeStorageKey(userId);
    localStorage.setItem(storageKey, JSON.stringify(size));
  } catch (error) {
    console.warn("Failed to save UniQ modal size to localStorage:", error);
  }
};

/**
 * Calculates the initial position for the modal based on the viewport size.
 * @param width - The width of the modal.
 * @param height - The height of the modal.
 * @returns An object containing the initial x and y positions for the modal.
 */
export const getInitialModalPosition = (
  width: number,
  height: number,
): { x: number; y: number } => {
  if (typeof window === "undefined") {
    return { x: MODAL_RIGHT_OFFSET, y: MODAL_BOTTOM_OFFSET };
  }

  return {
    x: Math.max(window.innerWidth - width - MODAL_RIGHT_OFFSET, 0),
    y: Math.max(window.innerHeight - height - MODAL_BOTTOM_OFFSET, 0),
  };
};

/**
 * Clamps the modal position within the viewport.
 *
 * @param width - The width of the modal.
 * @param height - The height of the modal.
 * @param x - The current x position of the modal.
 * @param y - The current y position of the modal.
 * @returns The clamped x and y positions.
 */
export const clampModalPosition = (
  width: number,
  height: number,
  x: number,
  y: number,
): { x: number; y: number } => {
  if (typeof window === "undefined") {
    return { x, y };
  }

  return {
    x: Math.min(Math.max(x, 0), Math.max(window.innerWidth - width, 0)),
    y: Math.min(Math.max(y, 0), Math.max(window.innerHeight - height, 0)),
  };
};
